import { QueryClient, useQuery, UseQueryOptions } from 'react-query';
import { UserRole, UsersMeResponse, WorkflowRole } from '../../sdk/types';
import { useSDK } from '../useSDK';
import { EMPTY_ARRAY } from '../../constants';
import { createSDK } from '../../sdk';

export const USERS_ME_QUERY_KEY = 'users-me-v3';
const USERS_ME_DEFAULT_QUERY_OPTIONS = {
  staleTime: Infinity,
};

type UseGetUsersMeProps<SelectorResponse> =
  | Omit<UseQueryOptions<UsersMeResponse, unknown, SelectorResponse>, 'queryFn' | 'queryKey'>
  | undefined;

const isAdminSelector = (usersMeResponse: UsersMeResponse) =>
  usersMeResponse.user.role === UserRole.projectAdmin;

export const useIsAdmin = () => {
  const { data: isAdmin } = useGetUsersMe({ select: isAdminSelector });
  return isAdmin;
};

const getCurrentUserRolesSelector = (usersMeResponse: UsersMeResponse) =>
  usersMeResponse.workflow?.roles;

export const useGetCurrentUserWorkflowRoles = () => {
  const { data: roles } = useGetUsersMe({ select: getCurrentUserRolesSelector });
  return roles || EMPTY_ARRAY;
};

const isManagerSelector = (usersMeResponse: UsersMeResponse) =>
  usersMeResponse.workflow?.roles?.includes(WorkflowRole.ProjectGC);

export const useIsManagerRoleExists = () => {
  const { data: isManager } = useGetUsersMe({ select: isManagerSelector });
  return isManager;
};

const getExternalUsersCountSelector = (usersMeResponse: UsersMeResponse) =>
  usersMeResponse.externalUsers?.length;

export const useGetExternalUsersCount = () => {
  const { data: count } = useGetUsersMe({ select: getExternalUsersCountSelector });
  return count || 0;
};

export const prefetchUsersMe = (queryClient: QueryClient, projectId: string) => {
  const sdk = createSDK({ projectId });
  return queryClient.prefetchQuery(
    [USERS_ME_QUERY_KEY],
    sdk.Users.me,
    USERS_ME_DEFAULT_QUERY_OPTIONS,
  );
};

export const useGetUsersMe = <SelectorResponse = UsersMeResponse,>(
  options?: UseGetUsersMeProps<SelectorResponse>,
) => {
  const sdk = useSDK();
  return useQuery<UsersMeResponse, unknown, SelectorResponse>([USERS_ME_QUERY_KEY], sdk.Users.me, {
    ...USERS_ME_DEFAULT_QUERY_OPTIONS,
    ...options,
  });
};
